import { faSearch, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cloneDeep, isEmpty } from 'lodash';
import { BaseSyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectControls } from '../../redux/controls';
import { loadItemDetailStart, selectItem } from '../../redux/itemDetail';
import { loadHTMLPreviewStart, loadQDSearchStart, resetForDownload, selectForDownload, selectIdsForDownload, selectItemRequest, selectQDSearchLoaded, selectQDSearchLoading, selectTabDataEntities, selectTabItemCount } from '../../redux/queryResults';
import { GridItem, GridItemEntities } from '../../redux/queryResults/models';
import { ASC, DESC } from '../../shared';
import ItemDetail from '../ItemDetail/ItemDetail';
import Modal from '../Modals/Modal';
import NQTCheckBox from '../NQTCheckBox/NQTCheckBox';
import { ICheckBox } from '../NQTCheckBox/NQTCheckBox.models';
import { ITEM_TYPE_TYPE } from '../QueryPanel/models';
import Spinner from '../Spinner/Spinner';
import { BLOCK, CC, DIFFICULTY, GRADE, INITIAL_SORT, IOrderBy, ListViewProps, QUESTION_ID, Sortable, TYPE, YEAR } from './models';
import './ListView.scss';
import { abstractComparator } from './models/ListView.utils';
import ga4 from '../../shared/analyticsEvents';

export const ListView = ({hidden, tabData, setTabData, showFullTable}: ListViewProps): JSX.Element => {
  const dispatch = useDispatch();
  const controls = useSelector(selectControls);
  const itemRequest = useSelector(selectItemRequest);
  const gridItemEntities = useSelector(selectTabDataEntities);
  const idsForDownload = useSelector(selectIdsForDownload);
  const itemCount = useSelector(selectTabItemCount);
  const searchLoading = useSelector(selectQDSearchLoading);
  const searchLoaded = useSelector(selectQDSearchLoaded);

  const currentItemDetail = useSelector(selectItem);

  const qdSearchEl = useRef<HTMLInputElement>(null);

  const [tabDataEntities, setTabDataEntities] = useState<GridItemEntities|null>(null);

  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(0);

  const [selectedIds, setSelectedIds] = useState<GridItemEntities|null>(null);
  const manuallySetMaxHt = {
    maxHeight: '214px'
  };
  //Header State
  const [selectAll, setSelectAll] = useState<ICheckBox>({isSelected: false});
  const [selectAllIndeterminate, setSelectAllIndeterminate] = useState(false);
  const [sort, setSort] = useState<IOrderBy>(INITIAL_SORT);
  const [searchRequested, setSearchRequested] = useState<boolean>(false);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalHeader, setModalHeader] = useState<string>('');
  const [modalContent, setModalContent] = useState<string>('');

  //#region useEffects

  // setSort on componentMount
  useEffect(() => {
    setSort(INITIAL_SORT);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // set tabDataEntities when response loads from server
  // tracks focal point
  useEffect(() => {
    gridItemEntities && setTabDataEntities(cloneDeep(gridItemEntities));
  }, [gridItemEntities]);

  // set tabData array once entities are loaded,
  // sets HTMLPreview on initial load,
  // sets ItemDetail for the first tabData load,
  // sorts tabData on first load based on INITIAL_SORT
  // tracks isSelected attributes for download
  useEffect(() => {
    if (tabDataEntities && itemRequest) {
      const sortedTabData = cloneDeep(Object.keys(tabDataEntities).map(id => tabDataEntities[parseInt(id, 10)]).sort(abstractComparator(INITIAL_SORT)));
      let changed = false;
      setTabData(currState => {
        if (currState && currState.length === sortedTabData.length) {
          currState.forEach((gi, i) => {
            if (gi.itemTableIDAsInt !== sortedTabData[i].itemTableIDAsInt) {
              changed = true;
            }
          });
          if (changed) {
            return sortedTabData;
          } else {
            return currState;
          }
        } else {
          changed = true;
          return sortedTabData;
        }
      });
      if (sortedTabData.length > 0 && !searchLoading && !isEmpty(gridItemEntities)) {
        if (changed) {
          const ItemList = sortedTabData.map(gi => gi.itemTableID);
          ItemList &&
          dispatch(
            loadHTMLPreviewStart({...itemRequest, ItemList})
          );
        }
        // only execute if different from store
        currentItemDetail?.itemTableIDAsInt !== sortedTabData[0].itemTableIDAsInt &&
        dispatch(
          loadItemDetailStart({
            gridItem: cloneDeep(sortedTabData[0]),
            request: cloneDeep(itemRequest)
          })
        );
      }
    }
  // don't include searchLoading, else it will update prematurely
  // don't include gridItemEntities, else its change will trigger infinitely
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, tabDataEntities]);

  // sort tabData, reset focal question, and call loadItemDetail
  useEffect(() => {
    if (tabData && itemRequest && tabData.length > 0) {
      const sortedTabData = cloneDeep(tabData).sort(abstractComparator(sort));
      setTabData(sortedTabData);
      setSelectedItemIndex(0);
      // only execute if different from store
      currentItemDetail?.itemTableIDAsInt !== sortedTabData[0].itemTableIDAsInt &&
      dispatch(
        loadItemDetailStart({
          gridItem: cloneDeep(sortedTabData[0]),
          request: cloneDeep(itemRequest)
        })
      );

      const ItemList = sortedTabData.map(gi => gi.itemTableID);
      ItemList &&
      dispatch(
        loadHTMLPreviewStart({...itemRequest, ItemList})
      );

    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  // set searchRequested when searchLoading is true
  useEffect(() => {
    if (searchLoading) {
      setSearchRequested(true);
    }
  }, [searchLoading]);

  // set searchBar error modal if searchRequest returns 0 itemCount
  // set searchbar value to empty string
  useEffect(() => {
    if (searchRequested && !searchLoading) {
      if (qdSearchEl.current && itemRequest && !itemCount) {
        qdSearchEl.current.value = '';
        dispatch(
          loadQDSearchStart({
            ...itemRequest,
            Keyword: ''
          })
        );
        if (controls) {
          setModalHeader(controls?.SrchDefault?.controlText);
          setModalContent('your keyword search didn\'t return any results');
          setIsModalOpen(true);
        }
      }

      setSearchRequested(false);
    }
  }, [controls, dispatch, itemCount, itemRequest, searchLoading, searchRequested]);


  // uncheck parent selectAll checkbox and reset selectedIds for download
  useEffect(() => {
    if (searchLoaded) {
      setSelectAll({isSelected: false});
      setSelectAllIndeterminate(false);
      setSelectedIds(null);
    }
  }, [searchLoaded]);

  // update selected Ids For Download when selectedIds are added or removed
  useEffect(() => {
    if (selectedIds && !isEmpty(selectedIds)) {
      dispatch(
        selectForDownload(cloneDeep(selectedIds))
      );
    } else {
      if (!isEmpty(idsForDownload)) {
        dispatch(
          resetForDownload()
        );
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedIds]);

  //#endregion useEffects

  //#region Function Callbacks

  const selectAllUpdateHandler = useCallback(() => {
    let newIds = {...selectedIds};

    if (selectAll.isSelected) {
      tabData && setTabData(cloneDeep(tabData).map(item => {
        item.isSelected = true;
        newIds[item.itemTableID] = item;
        return item;
      }));
      // select all if it's indeterminate
    } else if (selectAllIndeterminate) {
      setSelectAllIndeterminate(false);
      setSelectAll({isSelected: true});
      tabData && setTabData(cloneDeep(tabData).map(item => {
        item.isSelected = true;
        newIds[item.itemTableID] = item;
        return item;
      }));
      // deselect all
    } else {
      tabData && setTabData(cloneDeep(tabData).map(item => {
        item.isSelected = false;
        return item;
      }));
      newIds = {};
    }
    setSelectedIds(newIds);
  }, [selectedIds, selectAll.isSelected, selectAllIndeterminate, tabData, setTabData]);

  const sortByHandler = useCallback((type: Sortable) => {
    if (type === sort.selected) {
      sort.orderBy === ASC ?
        setSort({...sort, orderBy: DESC}) :
        setSort({...sort, orderBy: ASC});
    } else {
      setSort({selected: type, orderBy: DESC});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[sort]);

  // TODO: Optimize lookup when components are further developed
  /**
   * Syncs selectAll state ie. selected, not-selected, indeterminate
   * and update downloadEntities
   */
  const selectItemUpdateHandler = useCallback((item: GridItem) => {
    // sync selectAll
    const selected = [];
    tabData?.forEach(item => {
      if(item.isSelected) selected.push(item.itemTableID);
    });

    if (tabData?.length === selected.length || selected.length === 0) {
      setSelectAllIndeterminate(false);
      tabData?.length === selected.length && setSelectAll({isSelected: true});
      selected.length === 0 && setSelectAll({isSelected: false});
    } else {
      setSelectAllIndeterminate(true);
    }

    // update downloadEntities
    const downloadEntities = {...selectedIds};
    item.isSelected ?
      downloadEntities[item.itemTableID] = item :
      delete downloadEntities[item.itemTableID];

    setSelectedIds(downloadEntities);

  }, [selectedIds, tabData]);

  const selectRowUpdateHandler = useCallback((e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, ind: number, item: GridItem): void => {
    const target = e.target as (EventTarget & HTMLTableRowElement);
    if (
      target.className !== 'nqt-checkbox' &&
      target.className !== 'nqt-checkbox-label' &&
      target.className !== 'nqt-checkbox-input' &&
      target.className !== 'grid-item-anchor' &&
      target.nodeName !== 'svg' &&
      target.nodeName !== 'path' &&
      itemRequest
    ) {
      if (item && selectedItemIndex !== ind) {
        setSelectedItemIndex(ind);
        // only execute if different from store
        currentItemDetail?.itemTableIDAsInt !== item.itemTableIDAsInt &&
        dispatch(
          loadItemDetailStart({
            gridItem: cloneDeep(item),
            request: cloneDeep(itemRequest)
          })
        );

        const activeTabEl = document.querySelector('#pills-tab .active') as HTMLElement;
        if (activeTabEl) {
          activeTabEl.focus();
        }
      }
    }
  }, [itemRequest, selectedItemIndex, currentItemDetail?.itemTableIDAsInt, dispatch]);

  const keyUpRowUpdateHandler = useCallback((e: React.KeyboardEvent<HTMLTableRowElement>, ind: number, item: GridItem): void => {
    if (e.key !== 'Enter') {
      return;
    }
    const target = e.target as (EventTarget & HTMLTableRowElement);
    if (
      target.className !== 'nqt-checkbox' &&
      target.className !== 'nqt-checkbox-label' &&
      target.className !== 'nqt-checkbox-input' &&
      target.className !== 'grid-item-anchor' &&
      target.nodeName !== 'svg' &&
      target.nodeName !== 'path' &&
      itemRequest
    ) {
      if (item && selectedItemIndex !== ind) {
        setSelectedItemIndex(ind);
        // only execute if different from store
        currentItemDetail?.itemTableIDAsInt !== item.itemTableIDAsInt &&
        dispatch(
          loadItemDetailStart({
            gridItem: cloneDeep(item),
            request: cloneDeep(itemRequest)
          })
        );

        const activeTabEl = document.querySelector('#pills-tab .active') as HTMLElement;
        if (activeTabEl) {
          activeTabEl.focus();
        }
      }
    }
  }, [itemRequest, selectedItemIndex, currentItemDetail?.itemTableIDAsInt, dispatch]);

  const setItemDetailHandler = useCallback((ind: number) => {
    if (tabData && ind > -1 && ind <= tabData.length && itemRequest) {
      setSelectedItemIndex(ind);
      // only execute if different from store
      currentItemDetail?.itemTableIDAsInt !== tabData[ind].itemTableIDAsInt &&
      dispatch(
        loadItemDetailStart({
          gridItem: cloneDeep(tabData[ind]),
          request: cloneDeep(itemRequest)
        })
      );

      // Give time for new selected-item-detail to update in DOM
      setTimeout(() => {
        const selected = document.querySelector('.nqt-list-view .selected-row.selected-item-detail');
        selected && selected.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
        if (!showFullTable) {
          const tableBodyCollection = document.getElementsByClassName('tab-data-body') as HTMLCollectionOf<HTMLDivElement>;
          const tableBody = tableBodyCollection[0];
          if (tableBody) {
            const tr = tableBody.getElementsByClassName('selected-item-detail')[0] as HTMLTableRowElement;
            if (tr) {
              tr.scrollIntoView();
              //get parent item
              tableBody.style.maxHeight = tr.clientHeight.toString() + 'px';
              // align scroll to the top of the selected item
              tableBody.scrollTop = tr.offsetTop;
            } else {
              // scroll to the top of the table
              tableBody.scrollTop = 0;
              // get first row in the table
              const tr1 = tableBody.getElementsByTagName('tr')[0];
              tableBody.style.maxHeight = tr1.clientHeight.toString() + 'px';
            }
          }
        }
      }, 0);
    }
  }, [tabData, itemRequest, currentItemDetail?.itemTableIDAsInt, dispatch, showFullTable]);

  const onSearchHandler = useCallback((e: BaseSyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (itemRequest && qdSearchEl.current) {
      const clonedRequest = cloneDeep(itemRequest);
      clonedRequest.Keyword = qdSearchEl.current.value;
      dispatch(
        loadQDSearchStart(clonedRequest)
      );
      ga4.onGA4PerformQuery(clonedRequest.Keyword);
    }
  }, [dispatch, itemRequest]);

  //#endregion Function Callbacks

  return (
    <div className={`nqt-list-view${hidden ? ' d-none': ''}`}>
      {searchLoading && <Spinner size={80}/>}
      <Modal
        headerText={modalHeader}
        bodyText={modalContent} open={isModalOpen} onClose={() => setIsModalOpen(false)}/>
      <div className='tab-data-header'>
        <table className='table table-head'>
          <caption>Questions Table</caption>
          <colgroup>
            <col className='col-select-all'/>
            <col className='col-question'/>
            <col className='col-question-desc'/>
            <col className='col-grade'/>
            <col className='col-year'/>
            <col className='col-block'/>
            <col className='col-type'/>
            <col className='col-cc'/>
            <col className='col-dl'/>
            <col className='col-question-id'/>
            <col className='col-blank'/>
          </colgroup>
          <thead>
            <tr id='list-view-header' tabIndex={0}>
              <th className='table-header' colSpan={1}>
                <NQTCheckBox containerClass='tab-data'
                  label='All'
                  hideLabel={true}
                  checkBoxObj={selectAll}
                  id='table-header--select-all'
                  isIndeterminate={selectAllIndeterminate}
                  type={ITEM_TYPE_TYPE}
                  updateHandler={selectAllUpdateHandler}/>
              </th>
              <th className='table-header tab-data-hidetext' colSpan={1}>#</th>
              <th className='table-header' colSpan={1}>
                <form className='question-desc-search-container' onSubmit={onSearchHandler}>
                  <label htmlFor='q-d-search' className='question-desc-search'>{controls?.SrchDefault?.controlText}</label>
                  <input type='text'
                    id='q-d-search'
                    aria-labelledby="q-d-search"
                    ref={qdSearchEl}
                    placeholder={controls?.SrchDefault?.controlText}
                    defaultValue={
                      itemRequest && itemRequest.Keyword ?
                        itemRequest.Keyword :
                        ''
                    }/>
                  <button
                    title={controls ? controls?.SubmitSearch?.controlText : ''}
                    disabled={searchLoading}
                    name={controls?.SrchDefault?.controlText}
                    type='submit'
                    className='q-d-search-btn'
                    aria-label={controls?.SrchDefault?.controlText + ' button'}>
                    <FontAwesomeIcon className='fa-search' icon={faSearch} />
                  </button>
                </form>
              </th>
              <th className='table-header' colSpan={1}>
                <button className='sort-by'
                  onClick={() => sortByHandler(GRADE)}>
                  <span className='sort-by-label'>
                    {
                      itemRequest && itemRequest.SystemID === '3' ?
                        controls?.HeadAge?.controlText :
                        controls?.HeadGrade?.controlText
                    }
                  </span>
                  <span className='sort-by-icon'>
                    {
                      sort.selected === GRADE && sort.orderBy === DESC ?
                        <FontAwesomeIcon className='fa-sort-up' icon={faSortUp}/> :
                        <FontAwesomeIcon className='fa-sort-down' icon={faSortDown}/>
                    }
                  </span>
                </button>
              </th>
              <th className='table-header' colSpan={1}>
                <button className='sort-by'
                  onClick={() => sortByHandler(YEAR)}>
                  <span className='sort-by-label'>
                    {controls?.Year?.controlText}
                  </span>
                  <span className='sort-by-icon'>
                    {
                      sort.selected === YEAR && sort.orderBy === DESC ?
                        <FontAwesomeIcon className='fa-sort-up' icon={faSortUp}/> :
                        <FontAwesomeIcon className='fa-sort-down' icon={faSortDown}/>
                    }
                  </span>
                </button>
              </th>
              <th className='table-header' colSpan={1}>
                <button className='sort-by'
                  onClick={() => sortByHandler(BLOCK)}>
                  <span className='sort-by-label'>
                    {controls?.HeadBlock?.controlText}
                  </span>
                  <span className='sort-by-icon'>
                    {
                      sort.selected === BLOCK && sort.orderBy === DESC ?
                        <FontAwesomeIcon className='fa-sort-up' icon={faSortUp}/> :
                        <FontAwesomeIcon className='fa-sort-down' icon={faSortDown}/>
                    }
                  </span>
                </button>
              </th>
              <th className='table-header' colSpan={1}>
                <button className='sort-by'
                  onClick={() => sortByHandler(TYPE)}>
                  <span className='sort-by-label'>
                    {controls?.HeadType?.controlText}
                  </span>
                  <span className='sort-by-icon'>
                    {
                      sort.selected === TYPE && sort.orderBy === DESC ?
                        <FontAwesomeIcon className='fa-sort-up' icon={faSortUp}/> :
                        <FontAwesomeIcon className='fa-sort-down' icon={faSortDown}/>
                    }
                  </span>                </button>
              </th>
              <th className='table-header' colSpan={1}>
                <button className='sort-by'
                  onClick={() => sortByHandler(CC)}>
                  <span className='sort-by-label'>
                    {controls?.HeadCC?.controlText}
                  </span>
                  <span className='sort-by-icon'>
                    {
                      sort.selected === CC && sort.orderBy === DESC ?
                        <FontAwesomeIcon className='fa-sort-up' icon={faSortUp}/> :
                        <FontAwesomeIcon className='fa-sort-down' icon={faSortDown}/>
                    }
                  </span>
                </button>
              </th>
              <th className='table-header' colSpan={1}>
                <button className='sort-by'
                  onClick={() => sortByHandler(DIFFICULTY)}>
                  <span className='sort-by-label'>
                    {controls?.HeadDifficulty?.controlText}
                  </span>
                  <span className='sort-by-icon'>
                    {
                      sort.selected === DIFFICULTY && sort.orderBy === DESC ?
                        <FontAwesomeIcon className='fa-sort-up' icon={faSortUp}/> :
                        <FontAwesomeIcon className='fa-sort-down' icon={faSortDown}/>
                    }
                  </span>
                </button>
              </th>
              <th className='table-header' colSpan={1}>
                <button className='sort-by'
                  onClick={() => sortByHandler(QUESTION_ID)}>
                  <span className='sort-by-label'>
                    {controls?.HeadQuestionID?.controlText}
                  </span>
                  <span className='sort-by-icon'>
                    {
                      sort.selected === QUESTION_ID && sort.orderBy === DESC ?
                        <FontAwesomeIcon className='fa-sort-up' icon={faSortUp}/> :
                        <FontAwesomeIcon className='fa-sort-down' icon={faSortDown}/>
                    }
                  </span>
                </button>
              </th>
              <td/>
            </tr>
          </thead>
        </table>
      </div>
      <div style={manuallySetMaxHt} className={`tab-data-body${tabData?.length === 0 ? ' d-none': ''}`}>
        <table id='nqt-question-item-list' className='table table-body'>
          <colgroup>
            <col className='col-select-all'/>
            <col className='col-question'/>
            <col className='col-question-desc'/>
            <col className='col-grade'/>
            <col className='col-year'/>
            <col className='col-block-type'/>
            <col className='col-block-type'/>
            <col className='col-cc'/>
            <col className='col-dl'/>
            <col className='col-question-id'/>
          </colgroup>
          <tbody>
            {
              tabData &&
              tabData.map((gi, ind) => (
                <tr key={`gi-${ind}`}
                  className={selectedItemIndex === ind ? 'selected-row selected-item-detail' : ''}
                  id={`nqt-lv-gi-${ind}`}
                  tabIndex={0}
                  onClick={(e) => selectRowUpdateHandler(e, ind, gi)}
                  onKeyUp={(e) => keyUpRowUpdateHandler(e, ind, gi)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      const length = tabData.length;
                      if (ind < length - 1) {
                        const nextGridItem = document.getElementById('nqt-lv-gi-'+ (ind+1));
                        if (nextGridItem) {
                          e.preventDefault();
                          nextGridItem.focus();
                        }
                      } else {
                        return;
                      }
                    } else if (e.key === 'ArrowUp') {
                      if (ind === 0) {
                        return;
                      }
                      const prevGridItem = document.getElementById('nqt-lv-gi-'+ (ind-1));
                      if (prevGridItem) {
                        e.preventDefault();
                        prevGridItem.focus();
                      }
                    } 
                  }}>
                  <td colSpan={1}>
                    <NQTCheckBox containerClass='tab-data'
                      checkBoxObj={gi}
                      id={`gi-${ind}`}
                      hideLabel={true}
                      label={gi.description}
                      type={ITEM_TYPE_TYPE}
                      updateHandler={() => selectItemUpdateHandler(gi)}/>
                  </td>
                  <td colSpan={1}>{ind + 1}</td>
                  <td className='grid-item-left' colSpan={1} dangerouslySetInnerHTML={{__html:gi.description}}></td>
                  <td colSpan={1}>{gi.grade}</td>
                  <td colSpan={1}>{gi.year}</td>
                  <td colSpan={1}>{gi.blockID}</td>
                  <td colSpan={1}>
                    {
                      !gi.itemTypeDescControl ?
                        gi.displayType :
                        <button id={`gi-anchor-${ind}`} className="grid-item-anchor"
                          onClick={() => {
                            if (controls){
                              try {
                                setModalHeader(controls[gi.itemTypeDescControl.replace(/Text/, 'Title')]?.controlText);
                                setModalContent(controls[gi.itemTypeDescControl]?.controlText);
                                setIsModalOpen(true);
                              } catch (e) {
                                console.error(e);
                              }
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Tab' && !e.shiftKey) {
                              const targetGridItem = document.getElementById('nqt-lv-gi-'+ ind) as HTMLButtonElement;
                              const questionDetailButton = document.getElementById('pills-question-tab') as HTMLElement;
                              if (targetGridItem.classList.contains('selected-row')) {
                                e.preventDefault();
                                questionDetailButton.focus();
                              }
                            }
                          }}>
                          {gi.displayType}
                        </button>
                    }
                  </td>
                  <td className='grid-item-left' colSpan={1}>{gi.mainClassification}</td>
                  <td colSpan={1}>{gi.difficulty}</td>
                  <td colSpan={1}>{gi.questionID}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      {
        (tabData && tabData.length > 0) &&
        <ItemDetail
          ItemIndex={selectedItemIndex + 1}
          SetItemDetailHandler={setItemDetailHandler}/>
      }
    </div>
  );
};

export default ListView;